import type { TPostAttachment } from '@onesource/schemas';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  ATTACHMENT_ACCEPT,
  AttachmentTooLargeError,
  MAX_ATTACHMENTS,
  attachmentToItem,
  defaultAttachmentLimits,
  detectAttachmentType,
  extractUploadErrorMessage,
  formatBytes,
  isAcceptedAttachment,
  isDecodableImage,
  itemToAttachment,
  maxAttachmentBytesFor,
  readVideoDuration,
  uploadPostFile,
  type PostAttachmentItem,
} from './post-attachment-upload';

let attachmentIdCounter = 0;
const nextAttachmentId = () => {
  attachmentIdCounter += 1;
  return `att-${attachmentIdCounter}`;
};

/** A picked file paired with the video length we measured for it (if any). */
interface PreparedFile {
  file: File;
  durationSeconds?: number;
}

/**
 * Manages the attachment list for the post composer and the inline edit form:
 * picking + uploading new files (presigned-PUT R2), removing items, and
 * exposing the resolved `attachments` payload. Seed `initial` (existing
 * attachments) to edit a post's media.
 *
 * `canManage` reflects whether the user holds `space.manage` on the space being
 * posted to. Members get a per-file size cap and a maximum video length;
 * managers have neither. Defaults to `true` for admin surfaces with no space in
 * scope (e.g. the dashboard's post editor).
 *
 * The ceilings used here come from the compiled-in defaults. members-api is
 * still the authority — it enforces its own env-configured values at presign —
 * so these only decide what the picker blocks locally. Keep
 * MEMBER_MAX_ATTACHMENT_BYTES / MEMBER_MAX_VIDEO_DURATION_SEC in schemas in step
 * with the deployed env vars, or the picker will be stricter than the server.
 */
export const usePostAttachments = (initial?: TPostAttachment[] | null, options?: { canManage?: boolean }) => {
  const canManage = options?.canManage ?? true;
  const { maxBytes, maxDurationSec } = defaultAttachmentLimits(canManage);

  const [items, setItems] = useState<PostAttachmentItem[]>(() => (initial ?? []).map(attachmentToItem));
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Revoke object URLs for freshly picked files on unmount.
  useEffect(() => {
    return () => {
      setItems(prev => {
        prev.forEach(item => {
          if (item.preview !== item.url) {
            URL.revokeObjectURL(item.preview);
          }
        });
        return prev;
      });
    };
  }, []);

  /**
   * React does not await an async onChange handler, so anything that throws in
   * here becomes an unhandled rejection: the picker appears to do nothing at
   * all, with no preview and no message, and the only trace is in the console.
   * Wrap the whole thing so a failure is always surfaced to the user.
   */
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      await selectFiles(e);
    } catch (selectError: unknown) {
      setError(
        `Could not attach that file: ${selectError instanceof Error ? selectError.message : String(selectError)}`,
      );
    }
  };

  const selectFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      return;
    }
    setError(null);

    // Snapshot into a real array BEFORE touching the input. `files` is a live
    // reference to the input's FileList, and resetting `value` empties it — read
    // it afterwards and every pick silently yields zero files.
    // oxlint-disable-next-line unicorn/prefer-spread -- FileList spread needs downlevelIteration, not enabled in this package's tsconfig
    const picked = Array.from(files);

    // Now safe to clear, so the same file can be re-picked after a rejection
    // (an unchanged value fires no change event). Metadata reading below is
    // async, so this cannot wait until the end.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    const remainingSlots = Math.max(0, MAX_ATTACHMENTS - items.length);
    const selected = picked.slice(0, remainingSlots);

    const messages: string[] = [];

    // Genuinely at the cap — every filter below would be a no-op, so say so
    // rather than appearing to ignore the pick.
    if (selected.length === 0) {
      setError(`You can attach at most ${MAX_ATTACHMENTS} files to a post.`);
      return;
    }
    if (selected.length < picked.length) {
      messages.push(`Only ${selected.length} more file(s) can be attached, so the rest were skipped.`);
    }

    // Accepted types are images, PDFs and videos — drop the rest. A file the OS
    // gives no MIME type for (`file.type === ''`) lands here too, so name the
    // file rather than leaving the user guessing which one was dropped.
    const permitted = selected.filter(file => isAcceptedAttachment(file));
    if (permitted.length < selected.length) {
      const skipped = selected.filter(file => !isAcceptedAttachment(file)).map(file => file.name);
      messages.push(`Only images, PDFs, and videos are allowed — skipped ${skipped.join(', ')}.`);
    }

    const withinSize = maxBytes === null ? permitted : permitted.filter(file => file.size <= maxBytes);
    if (maxBytes !== null && withinSize.length < permitted.length) {
      messages.push(`Some files are over the ${formatBytes(maxBytes)} limit and were skipped.`);
    }

    // Measure video length before uploading: the presign request carries it, and
    // catching an over-long clip here saves the user waiting out a transfer the
    // server would refuse anyway. An unreadable duration passes through.
    // Concurrently, so several files do not serialise their probe timeouts.
    const measured = await Promise.all(
      withinSize.map(async file => {
        if (file.type.startsWith('video/')) {
          return { file, durationSeconds: await readVideoDuration(file), decodable: true };
        }
        if (file.type.startsWith('image/')) {
          return { file, durationSeconds: null, decodable: await isDecodableImage(file) };
        }
        return { file, durationSeconds: null, decodable: true };
      }),
    );

    // A file that claims to be an image but will not decode is corrupt or
    // misnamed. Nothing downstream inspects the bytes, so this is the only
    // chance to catch it before it becomes a permanently broken tile.
    const undecodable = measured.filter(entry => !entry.decodable).map(entry => entry.file.name);
    if (undecodable.length > 0) {
      messages.push(`Could not read ${undecodable.join(', ')} — the file may be corrupt.`);
    }

    const prepared: PreparedFile[] = [];
    let tooLongCount = 0;
    for (const { file, durationSeconds } of measured.filter(entry => entry.decodable)) {
      if (maxDurationSec !== null && durationSeconds !== null && durationSeconds > maxDurationSec) {
        tooLongCount += 1;
        continue;
      }
      prepared.push({ file, ...(durationSeconds !== null && { durationSeconds }) });
    }
    if (tooLongCount > 0) {
      messages.push(`Videos must be ${maxDurationSec} seconds or less, so longer ones were skipped.`);
    }

    if (messages.length > 0) {
      setError(messages.join(' '));
    }

    const newItems: PostAttachmentItem[] = prepared.map(({ file, durationSeconds }) => {
      const id = nextAttachmentId();
      const preview = URL.createObjectURL(file);
      // Upload in the background; patch the item by id when done.
      // The ceiling is resolved from the picked file's type — compression never
      // changes an image into a video — and applied to the optimised bytes
      // inside uploadPostFile.
      uploadPostFile(file, {
        durationSeconds,
        maxBytes: maxAttachmentBytesFor({ mimeType: file.type }, { maxBytes, maxDurationSec }),
      })
        .then(({ url, mediaId, name, mimeType, size }) => {
          // Name, type and size are re-read from the uploaded file: images are
          // re-encoded to webp and PDFs are compressed before the PUT, so the
          // picked file's metadata no longer describes the stored object.
          setItems(prev =>
            prev.map(item =>
              item.id === id ? { ...item, uploading: false, url, mediaId, name, mimeType, size } : item,
            ),
          );
        })
        .catch((uploadError: unknown) => {
          setItems(prev => prev.filter(item => item.id !== id));
          URL.revokeObjectURL(preview);
          // The presign call rejects here on size, duration and monthly quota —
          // those reasons are the user's only signal, so surface them instead of
          // dropping the item without explanation. A file that is still too big
          // after compression is rejected locally and carries its own message.
          setError(
            uploadError instanceof AttachmentTooLargeError
              ? uploadError.message
              : (extractUploadErrorMessage(uploadError) ?? `Upload failed for ${file.name}.`),
          );
        });
      return {
        id,
        preview,
        uploading: true,
        type: detectAttachmentType(file),
        name: file.name,
        mimeType: file.type || undefined,
        size: file.size,
        durationSeconds,
      };
    });

    if (newItems.length > 0) {
      setItems(current => [...current, ...newItems]);
    }
  };

  const removeItem = (id: string) => {
    setItems(prev => {
      const item = prev.find(i => i.id === id);
      if (item && item.preview !== item.url) {
        URL.revokeObjectURL(item.preview);
      }
      return prev.filter(i => i.id !== id);
    });
  };

  // Replace all items, revoking object URLs of any in-flight uploads. Pass
  // existing attachments to (re)seed the edit form, or nothing to clear (e.g.
  // after a successful post).
  const reset = (next?: TPostAttachment[] | null) => {
    setItems(prev => {
      prev.forEach(item => {
        if (item.preview !== item.url) {
          URL.revokeObjectURL(item.preview);
        }
      });
      return (next ?? []).map(attachmentToItem);
    });
    setError(null);
  };

  const clear = () => reset();

  const attachments = useMemo<TPostAttachment[]>(
    () => items.map(itemToAttachment).filter((a): a is TPostAttachment => a !== null),
    [items],
  );

  const isUploading = items.some(item => item.uploading);
  const atLimit = items.length >= MAX_ATTACHMENTS;

  const openFilePicker = () => fileInputRef.current?.click();

  return {
    items,
    attachments,
    error,
    setError,
    isUploading,
    atLimit,
    fileInputRef,
    accept: ATTACHMENT_ACCEPT,
    /** Per-file byte ceiling, or `null` when uncapped — for composer hint text. */
    maxBytes,
    /** Video length ceiling in seconds, or `null` when uncapped. */
    maxDurationSec,
    handleFileSelect,
    removeItem,
    clear,
    reset,
    openFilePicker,
  };
};
